import useEventSubscriber from '@/lib/hooks/useEventSubscriber.ts'
import { toast } from '@/lib/hooks/useToast.ts'
import { useUserStore } from '@/store/user.ts'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { ProjectStagesUsersT } from '@/components/common/menu/lib/types/projectStagesUsers.ts'
import NotificationToastLink from '@/routes/projectStages/notifications/view/NotificationToastLink.tsx'
import { queryClient } from '@/lib/api-client.ts'
import {
  invalidateNotificationTabs,
  NotificationCountsT,
  notificationCountsKey,
} from '@/routes/projectStages/notifications/lib/actions/notificationsActions.ts'
import { SingleIssueEvent } from '@/routes/projectStages/issue/types/issue.ts'
import NotificationEventTypeString from '@/routes/projectStages/notifications/view/NotificationEventTypeString.tsx'

// Track recently shown toast event IRIs to prevent duplicates
// (React Query data can be stale, so we need local deduplication)
const TOAST_DEDUP_TIMEOUT_MS = 5000
const recentlyShownToasts = new Set<string>()

// Coalesce the burst of list-refetch invalidations a batch triage / rapid
// activity can produce into a single refetch.
const INVALIDATE_DEBOUNCE_MS = 400
const invalidateTimers = new Map<string, ReturnType<typeof setTimeout>>()

function debouncedInvalidate(stageId: string) {
  const existing = invalidateTimers.get(stageId)
  if (existing) clearTimeout(existing)
  invalidateTimers.set(
    stageId,
    setTimeout(() => {
      invalidateTimers.delete(stageId)
      invalidateNotificationTabs(stageId)
    }, INVALIDATE_DEBOUNCE_MS),
  )
}

export default function useNotificationsSubscriber(
  projectUsers: ProjectStagesUsersT | null | undefined,
  stageId: string | number | null | undefined,
) {
  const user = useUserStore(state => state.user)
  const setAllUnreadNotificationsCount = useProjectStageStore(
    state => state.setAllUnreadNotificationsCount,
  )

  const userIri = `/users/${user?.id}`
  const projectStageIri = `/project_stages/${stageId}`

  function handleNotificationToast(event: SingleIssueEvent) {
    const eventIri = event[`@id`]
    if (!eventIri) return

    // Don't surface a toast for events the current user triggered themselves —
    // they've already seen their own action; a confirming toast is redundant.
    if (event.createdBy === userIri) {
      return
    }

    // Local de-dup (rapid duplicate events for the same IRI).
    if (recentlyShownToasts.has(eventIri)) {
      return
    }

    recentlyShownToasts.add(eventIri)
    setTimeout(() => {
      recentlyShownToasts.delete(eventIri)
    }, TOAST_DEDUP_TIMEOUT_MS)

    toast({
      description: (
        <NotificationEventTypeString event={event} projectUsers={projectUsers} location={'toast'} />
      ),
      // @ts-expect-error api types are wrong
      title: event.issue?.name,
      action: <NotificationToastLink event={event} projectStageIri={projectStageIri} />,
    })
  }

  useEventSubscriber(
    `${userIri}${projectStageIri}/notifications`,
    (e: MessageEvent) => {
      let eventData: Record<string, unknown>
      try {
        eventData = JSON.parse(e.data)
      } catch {
        console.error('[Notifications] Failed to parse SSE data')
        return
      }

      // 'activity' (a genuinely new event) is toast-worthy; 'triage'
      // (archive/snooze/read echo) is silent — counts only. An absent action
      // means an old backend → treat as 'activity' (deploy-window fallback).
      const action = (eventData.action as string | undefined) ?? 'activity'

      const counts = eventData.counts as NotificationCountsT | undefined
      const unreadCount = eventData.unreadCount as
        | { issueType?: string; total?: number; count?: number }
        | undefined

      // Sync the authoritative counts payload into the counts query cache so
      // tab badges + the inbox total update without a refetch. Prefer the new
      // `counts` shape; fall back to the legacy `unreadCount.total`.
      if (counts && stageId != null) {
        queryClient.setQueryData<NotificationCountsT>(notificationCountsKey(stageId), counts)
        setAllUnreadNotificationsCount(counts.inbox)
      } else if (unreadCount?.total !== undefined) {
        setAllUnreadNotificationsCount(unreadCount.total)
      }

      // NOTE: the per-issue-type sidebar badge is deliberately NOT written from
      // the push. The Mercure `unreadCount.count` is a raw notification count,
      // but the badge must equal the issue-list blue dots (distinct list-visible
      // issues with an unread notification). It is refetched authoritatively from
      // the issue-types provider via the invalidation below.

      // Any push means the lists may have shifted (new event, or a triage on
      // another device) — reconcile the lists on a debounce.
      if (stageId != null) debouncedInvalidate(String(stageId))

      if (action !== 'activity') return

      const event = (eventData.notification as Record<string, unknown>)?.event as
        | SingleIssueEvent
        | undefined
      if (!event) return
      handleNotificationToast(event)
    },
    {
      onRecovery: () => {
        if (stageId != null) invalidateNotificationTabs(stageId)
      },
    },
  )
}
